import type { FormField } from '@lucid/lego';
import {
  closedSubStatusOptions,
  junkPrimaryReasons,
  notInterestedPrimaryReasons,
  notQualifiedPrimaryReasons,
  notSalesInquiryPrimaryReasons,
  secondaryReasonsByPrimary,
} from '../../../constants';

/**
 * Build form fields for engagement status (Contacting/Engaged)
 */
export const buildEngagementFormFields = (
  updatingToStatus: string,
  initialNextEngagementDate: string,
  handleCancel: () => void,
  isLoading?: boolean
): FormField[] => [
  {
    id: 'updatingStatusTo',
    fieldType: 'content',
    width: 'full',
    label: 'UPDATING STATUS TO',
    content: updatingToStatus,
  },
  {
    id: 'nextEngagementDate',
    fieldType: 'date_picker',
    name: 'nextEngagementDate',
    apiKey: 'nextEngagementDate',
    label: 'Next Engagement Date',
    required: true,
    width: 'full',
    size: 'medium',
    minDate: 'today',
    defaultValue: initialNextEngagementDate,
    validationMessages: {
      required: 'Next Engagement Date is required',
    },
  },
  {
    id: 'notes',
    fieldType: 'textarea',
    name: 'notes',
    apiKey: 'notes',
    label: 'Notes',
    placeholder: 'Enter Notes',
    width: 'full',
    rows: 4,
  },
  {
    id: 'buttonGroup',
    fieldType: 'buttonGroup',
    align: 'right',
    buttons: [
      {
        id: 'cancel',
        label: 'CANCEL',
        type: 'secondary',
        color: 'neutral',
        size: 'medium',
        onClick: handleCancel,
        disabled: isLoading,
        isFluid: true,
      },
      {
        id: 'save',
        label: isLoading ? 'SAVING...' : 'SAVE',
        type: 'primary',
        color: 'neutral',
        size: 'medium',
        disabled: isLoading,
        isFluid: true,
      },
    ],
  },
];

/**
 * Build form fields for closed status using dependency for conditional rendering
 */
export const buildClosedFormFields = (
  handleCancel: () => void,
  isLoading?: boolean
): FormField[] => [
  {
    id: 'updatingStatusTo',
    fieldType: 'content',
    width: 'full',
    label: 'UPDATING STATUS TO',
    content: 'Closed',
  },
  {
    id: 'subStatus',
    fieldType: 'dropdown',
    name: 'subStatus',
    apiKey: 'subStatus',
    label: 'Sub Status',
    required: true,
    width: 'full',
    options: closedSubStatusOptions,
    placeholder: 'Select Sub Status',
    validationMessages: {
      required: 'Sub Status is required',
    },
  },
  // Primary Reason for Junk (default to Invalid Email since it's the only option)
  {
    id: 'primaryCloseReason_junk',
    fieldType: 'dropdown',
    name: 'primaryCloseReason',
    apiKey: 'primaryCloseReason',
    label: 'Primary Reason',
    required: true,
    width: 'full',
    options: junkPrimaryReasons,
    defaultValue: 'invalid_email',
    placeholder: 'Select Reason',
    dependency: {
      name: 'subStatus',
      value: ['junk'],
    },
    validationMessages: {
      required: 'Primary Reason is required',
    },
  },
  // Primary Reason for Not Interested
  {
    id: 'primaryCloseReason_not_interested',
    fieldType: 'dropdown',
    name: 'primaryCloseReason',
    apiKey: 'primaryCloseReason',
    label: 'Primary Reason',
    required: true,
    width: 'full',
    options: notInterestedPrimaryReasons,
    placeholder: 'Select Reason',
    dependency: {
      name: 'subStatus',
      value: ['not_interested'],
    },
    validationMessages: {
      required: 'Primary Reason is required',
    },
  },
  // Primary Reason for Not Qualified
  {
    id: 'primaryCloseReason_not_qualified',
    fieldType: 'dropdown',
    name: 'primaryCloseReason',
    apiKey: 'primaryCloseReason',
    label: 'Primary Reason',
    required: true,
    width: 'full',
    options: notQualifiedPrimaryReasons,
    placeholder: 'Select Reason',
    dependency: {
      name: 'subStatus',
      value: ['not_qualified'],
    },
    validationMessages: {
      required: 'Primary Reason is required',
    },
  },
  // Primary Reason for Not a Sales Inquiry
  {
    id: 'primaryCloseReason_not_sales_inquiry',
    fieldType: 'dropdown',
    name: 'primaryCloseReason',
    apiKey: 'primaryCloseReason',
    label: 'Primary Reason',
    required: true,
    width: 'full',
    options: notSalesInquiryPrimaryReasons,
    placeholder: 'Select Reason',
    dependency: {
      name: 'subStatus',
      value: ['not_sales_inquiry'],
    },
    validationMessages: {
      required: 'Primary Reason is required',
    },
  },
  // Secondary Reason fields for each Primary Reason that has secondary options
  // Charging (shared by Not Interested and Not Qualified)
  {
    id: 'secondaryCloseReason_charging',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.charging,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['charging'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Confidence in Company
  {
    id: 'secondaryCloseReason_confidence_in_company',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.confidence_in_company,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['confidence_in_company'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Customer Experience
  {
    id: 'secondaryCloseReason_customer_experience',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.customer_experience,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['customer_experience'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Environmental
  {
    id: 'secondaryCloseReason_environmental',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.environmental,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['environmental'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Financing
  {
    id: 'secondaryCloseReason_financing',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.financing,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['financing'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Purchased a Different Vehicle
  {
    id: 'secondaryCloseReason_purchased_different_vehicle',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.purchased_different_vehicle,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['purchased_different_vehicle'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Service Network
  {
    id: 'secondaryCloseReason_service_network',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.service_network,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['service_network'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Timing
  {
    id: 'secondaryCloseReason_timing',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.timing,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['timing'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Vehicle Features
  {
    id: 'secondaryCloseReason_vehicle_features',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.vehicle_features,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['vehicle_features'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // B2B
  {
    id: 'secondaryCloseReason_b2b',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.b2b,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['b2b'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Trade-In
  {
    id: 'secondaryCloseReason_trade_in',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.trade_in,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['trade_in'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Other
  {
    id: 'secondaryCloseReason_other',
    fieldType: 'dropdown',
    name: 'secondaryCloseReason',
    apiKey: 'secondaryCloseReason',
    label: 'Secondary Reason',
    required: true,
    width: 'full',
    options: secondaryReasonsByPrimary.other,
    placeholder: 'Select Reason',
    dependency: {
      name: 'primaryCloseReason',
      value: ['other'],
    },
    validationMessages: {
      required: 'Secondary Reason is required',
    },
  },
  // Note: No secondary reason field for 'invalid_email', 'vendor', 'applicant', or 'media'
  // as they don't have secondary reasons
  {
    id: 'buttonGroup',
    fieldType: 'buttonGroup',
    align: 'right',
    buttons: [
      {
        id: 'cancel',
        label: 'CANCEL',
        type: 'secondary',
        color: 'neutral',
        size: 'medium',
        onClick: handleCancel,
        disabled: isLoading,
        isFluid: true,
      },
      {
        id: 'save',
        label: isLoading ? 'SAVING...' : 'SAVE',
        type: 'primary',
        color: 'neutral',
        size: 'medium',
        disabled: isLoading,
        isFluid: true,
      },
    ],
  },
];
